import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { CommissionTransaction } from '../../types';

const ResellerCommissions: React.FC = () => {
  const [commissions, setCommissions] = useState<CommissionTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadCommissions();
  }, [page]);

  const loadCommissions = async () => {
    setLoading(true);
    const response = await api.getResellerCommissions({ page, pageSize: 20 });
    if (response.success && response.data) {
      setCommissions(response.data.data);
      setTotalPages(response.data.totalPages);
    } else {
      setError(response.error || 'Failed to load commissions');
    }
    setLoading(false);
  };

  const filteredCommissions = statusFilter === 'all'
    ? commissions
    : commissions.filter(c => c.status === statusFilter);

  // Calculate stats
  const totalEarned = commissions.filter(c => c.status === 'paid').reduce((sum, c) => sum + c.commissionAmount, 0);
  const pending = commissions.filter(c => c.status === 'pending').reduce((sum, c) => sum + c.commissionAmount, 0);
  const approved = commissions.filter(c => c.status === 'approved').reduce((sum, c) => sum + c.commissionAmount, 0);

  if (loading && commissions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
          <p className="text-neutral-500 text-sm">Loading commissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">My Commissions</h1>
        <p className="text-neutral-500">Track your earnings from client subscriptions</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl">💰</span>
            <p className="text-neutral-400">Total Earned</p>
          </div>
          <p className="text-3xl font-bold text-emerald-400">৳{totalEarned.toLocaleString()}</p>
          <p className="text-neutral-500 text-sm mt-1">Paid commissions</p>
        </div>

        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl">✅</span>
            <p className="text-neutral-400">Approved</p>
          </div>
          <p className="text-3xl font-bold text-blue-400">৳{approved.toLocaleString()}</p>
          <p className="text-neutral-500 text-sm mt-1">Ready for payout</p>
        </div>

        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl">⏳</span>
            <p className="text-neutral-400">Pending</p>
          </div>
          <p className="text-3xl font-bold text-yellow-400">৳{pending.toLocaleString()}</p>
          <p className="text-neutral-500 text-sm mt-1">Awaiting approval</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['all', 'pending', 'approved', 'paid'].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              statusFilter === status
                ? status === 'paid'
                  ? 'bg-emerald-600 text-white'
                  : status === 'approved'
                    ? 'bg-blue-600 text-white'
                    : status === 'pending'
                      ? 'bg-yellow-600 text-white'
                      : 'bg-emerald-600 text-white'
                : 'bg-[#1a1a1a] border border-[#262626] text-neutral-400 hover:bg-[#262626] hover:text-white'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Commissions Table */}
      <div className="bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
        {filteredCommissions.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1a1a1a]">
                  <tr>
                    <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Client</th>
                    <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Subscription</th>
                    <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Rate</th>
                    <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Commission</th>
                    <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Status</th>
                    <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#262626]">
                  {filteredCommissions.map((commission) => (
                    <tr key={commission.id} className="hover:bg-[#1a1a1a] transition-colors">
                      <td className="p-4">
                        <p className="text-white font-medium">{commission.clientId.substring(0, 8)}...</p>
                      </td>
                      <td className="p-4">
                        <p className="text-neutral-400">৳{commission.subscriptionAmount.toLocaleString()}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-neutral-400">{commission.commissionRate}%</p>
                      </td>
                      <td className="p-4">
                        <p className="text-emerald-400 font-bold">৳{commission.commissionAmount.toLocaleString()}</p>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                          commission.status === 'paid'
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : commission.status === 'approved'
                              ? 'bg-blue-500/20 text-blue-400'
                              : commission.status === 'pending'
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : 'bg-red-500/20 text-red-400'
                        }`}>
                          {commission.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="text-neutral-400 text-sm">
                            {new Date(commission.createdAt).toLocaleDateString('en-BD')}
                          </p>
                          {commission.paidAt && (
                            <p className="text-neutral-500 text-xs">
                              Paid: {new Date(commission.paidAt).toLocaleDateString('en-BD')}
                            </p>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-4 border-t border-[#262626] flex items-center justify-between">
                <p className="text-neutral-500 text-sm">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 bg-[#1a1a1a] border border-[#262626] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#262626] transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 bg-[#1a1a1a] border border-[#262626] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#262626] transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-[#1a1a1a] rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">💰</span>
            </div>
            <p className="text-neutral-500">No commissions found</p>
            <p className="text-neutral-600 text-sm mt-1">Commissions will appear when your clients subscribe</p>
          </div>
        )}
      </div>

      {/* Payout Info */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
            <span className="text-2xl">💳</span>
          </div>
          <div>
            <h3 className="font-bold text-white mb-1">Commission Payout</h3>
            <p className="text-blue-400 text-sm">
              Approved commissions are paid out monthly via bKash/Nagad or bank transfer.
              Contact admin if you have any questions about your payouts.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResellerCommissions;
